// src/i18n.ts
// Język interfejsu: tłumaczenia z src/locales, wybór języka i jego zapamiętanie. Importowany w index.tsx przed App,
// żeby tłumaczenia były gotowe, zanim wczytają się widoki. Opis: docs/plan-jezyka.md
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import plCommon from './locales/pl/common';
import plWordsList from './locales/pl/words-list';
import plStudent from './locales/pl/student';
import plProduct from './locales/pl/product';
import plMemory from './locales/pl/memory';
import plGames from './locales/pl/games';
import plFolders from './locales/pl/folders';
import enCommon from './locales/en/common';
import enWordsList from './locales/en/words-list';
import enStudent from './locales/en/student';
import enProduct from './locales/en/product';
import enMemory from './locales/en/memory';
import enGames from './locales/en/games';
import enFolders from './locales/en/folders';

export const LANGUAGES = ['pl', 'en'] as const;
export type Language = typeof LANGUAGES[number];

// Nazwa języka w nim samym — swój język znajdzie i ktoś, kto nie rozumie bieżącego
export const LANGUAGE_NAMES: Record<Language, string> = {
  pl: 'Polski',
  en: 'English',
};

const DEFAULT_LANGUAGE: Language = 'pl';

// Wybór pamięta przeglądarka: ostatni na tym urządzeniu (dla ekranów przed zalogowaniem) i osobno dla każdego
// konta — uczeń, który przełączy język na laptopie nauczyciela, nie zmieni go w panelu nauczyciela
const DEVICE_KEY = 'language';
const accountKey = (userId: number) => `language.${userId}`;

const isLanguage = (value: string | null): value is Language => LANGUAGES.some((language) => language === value);

const readLanguage = (key: string): Language | null => {
  try {
    const value = localStorage.getItem(key);

    return isLanguage(value) ? value : null;
  } catch {
    return null;
  }
};

const storeLanguage = (key: string, language: Language) => {
  try {
    localStorage.setItem(key, language);
  } catch {
    // Bez pamięci przeglądarki wybór działa do odświeżenia strony
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      pl: {
        common: plCommon,
        'words-list': plWordsList,
        student: plStudent,
        product: plProduct,
        memory: plMemory,
        games: plGames,
        folders: plFolders,
      },
      en: {
        common: enCommon,
        'words-list': enWordsList,
        student: enStudent,
        product: enProduct,
        memory: enMemory,
        games: enGames,
        folders: enFolders,
      },
    },
    lng: readLanguage(DEVICE_KEY) ?? DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    // React sam zabezpiecza wstawiany tekst. Bez tego nazwa listy albo grupy z „&” czy cudzysłowem
    // pokazałaby się jako encja HTML
    interpolation: { escapeValue: false },
  });

const setDocumentLanguage = (language: string) => {
  document.documentElement.lang = language;
};

setDocumentLanguage(i18n.language);
i18n.on('languageChanged', setDocumentLanguage);

export const getLanguage = (): Language => (isLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE);

// Zapis dat i dni tygodnia (toLocaleDateString, Intl) w języku interfejsu
const DATE_LOCALES: Record<Language, string> = {
  pl: 'pl-PL',
  en: 'en-GB',
};

export const getDateLocale = (): string => DATE_LOCALES[getLanguage()];

// Wybór z przełącznika w górnym pasku. Bez userId (nikt niezalogowany) pamięta go tylko urządzenie
export const changeLanguage = (language: Language, userId?: number) => {
  storeLanguage(DEVICE_KEY, language);
  if (userId !== undefined) {
    storeLanguage(accountKey(userId), language);
  }
  i18n.changeLanguage(language);
};

// Po zalogowaniu: język zapamiętany dla konta, a gdy konto jeszcze nie wybierało — polski
export const applyAccountLanguage = (userId: number) => {
  const language = readLanguage(accountKey(userId)) ?? DEFAULT_LANGUAGE;

  storeLanguage(DEVICE_KEY, language);
  if (language !== i18n.language) {
    i18n.changeLanguage(language);
  }
};

export default i18n;
